import { useState } from 'react';
import { Plus } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { EmptyState } from '@/components/composed/EmptyState';
import { formatCalendarDay } from '@/lib/format';
import { canOfferAct } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import type { Entity, EntityRelationship } from '@/lib/api/domains/entities';
import { RecordRelationshipDialog } from './RecordRelationshipDialog';
import { EndRelationshipConfirm } from './EndRelationshipConfirm';

/*
 * The both-directions relationship view (E02.F04 R11). On an organization's own
 * record, this shows every relationship it is party to in EITHER direction
 * (AC-8885f7a6) — the view does not filter to relationships that originate from
 * this organization. Each row shows the relationship's dates, its kind and the
 * OTHER party (AC-09bbaadc), by name (relationships are never shown by id).
 * Relationships that have ENDED are shown alongside those in force, distinguished
 * by a state badge (AC-95208d87) — the server decides the in-force flag against
 * the work's business date (R3/R40); the FE never computes it.
 *
 * This view is also the PLACE a relationship is created, corrected and ended
 * (AC-91f1fc5a): "Record relationship" opens the create dialog here, and each
 * in-force row offers "End" here. Correction is end-and-new / cancel-and-new on
 * the server (SR-28); the view surfaces the end action and the record action
 * that together make a correction, never an in-place edit.
 *
 * Permission is a UX gate only: the create/end controls are offered per-act
 * from the server-sourced capabilities; the server is the authority and refuses
 * regardless. The standalone relationship act codes (`tpabilling.relationships.*`)
 * are an OPEN QUESTION for Architecture (Doug Shannon); the server refuses a
 * relationship write today via the entity-edit act plus the Kelly-org boundary,
 * so the courtesy gate reads that same admin boundary act
 * (`tpabilling.enrollment.edit`) rather than inventing an FE-only code
 * (E02.F04.R6, AC-8eba4768/AC-e0d3a229). A `tpabilling_user` (Viewer) is offered
 * neither Record relationship nor End; both remain readable (AC-13cd0301).
 */
export function EntityRelationshipsView({ entity }: { entity: Entity }) {
  const session = useAuthStore((s) => s.session);
  const canOfferRelationshipWrite = canOfferAct(
    session,
    'tpabilling.enrollment.edit',
  );
  const [recordOpen, setRecordOpen] = useState(false);
  const [ending, setEnding] = useState<EntityRelationship | null>(null);

  const relationships = entity.relationships;

  // The "other party" is whichever of the two is not this organization — so a
  // relationship pointing AT this organization still reads by the party on the
  // other end (both directions, AC-09bbaadc).
  function otherParty(rel: EntityRelationship): string {
    return rel.fromEntityId === entity.id ? rel.toName : rel.fromName;
  }

  function directionLabel(rel: EntityRelationship): string {
    return rel.fromEntityId === entity.id ? 'to' : 'from';
  }

  return (
    <section aria-label="Relationships">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-medium">Relationships</h2>
        {canOfferRelationshipWrite ? (
          <Button size="sm" onClick={() => setRecordOpen(true)}>
            <Plus className="size-4" />
            Record relationship
          </Button>
        ) : null}
      </div>

      {relationships.length === 0 ? (
        <EmptyState
          title="No relationships"
          description="This organization is party to no relationships yet."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Other party</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead>End</TableHead>
              <TableHead>State</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {relationships.map((rel) => (
              <TableRow key={rel.id}>
                <TableCell>{rel.kind}</TableCell>
                <TableCell>{otherParty(rel)}</TableCell>
                <TableCell>{directionLabel(rel)}</TableCell>
                <TableCell>{formatCalendarDay(rel.effectiveDate)}</TableCell>
                <TableCell>
                  {rel.endDate ? formatCalendarDay(rel.endDate) : '—'}
                </TableCell>
                <TableCell>
                  {rel.inForce ? (
                    <StatusBadge label="In force" tone="success" />
                  ) : rel.canceled ? (
                    <StatusBadge label="Canceled" tone="neutral" />
                  ) : (
                    <StatusBadge label="Ended" tone="neutral" />
                  )}
                </TableCell>
                <TableCell>
                  {canOfferRelationshipWrite && rel.inForce ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEnding(rel)}
                    >
                      End
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <RecordRelationshipDialog
        entity={entity}
        open={recordOpen}
        onOpenChange={setRecordOpen}
      />

      {ending ? (
        <EndRelationshipConfirm
          entity={entity}
          relationship={ending}
          endDate={ending.effectiveDate}
          open={Boolean(ending)}
          onOpenChange={(open) => {
            if (!open) setEnding(null);
          }}
        />
      ) : null}
    </section>
  );
}
